import { useState, useRef, useEffect, memo } from 'react';
import { Tooltip } from 'components';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import styles from './styles.module.scss';
import WithLink from 'components/WithLink';
import useTranslation from 'hooks/useTranslation';
import { ChevronDownIcon } from 'assets/icons';
import { useDispatch, useSelector } from 'react-redux';
import { setSecondMenuCollapsed } from 'redux/layout/layout.actions';

const getIsSideMenuCollapsed = ({ layoutData }) =>
  layoutData.isSideMenuCollapsed;

function MenuItem({
  text,
  iconUrl,
  url,
  expandable,
  subItem,
  setShowMenu,
  hasNotifBadge,
  subscript,
  notifBadgeTooltipText,
  isExternalLink,
}) {
  const isSideMenuCollapsed = useSelector(getIsSideMenuCollapsed);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const firstLinkUrlPathname = url;
  const firstPageUrlPathname = location.pathname;
  const isActive = firstLinkUrlPathname === firstPageUrlPathname;
  const contentRef = useRef(null);
  const dispatch = useDispatch();
  const isMdDown = useMediaQuery({
    query: '(max-width: 992px)',
  });
  const { getAppLocale } = useTranslation();

  const notifBadgeTooltipStyle = {
    width: '180px',
  };

  const IconUrl = iconUrl;

  const handleToggleItemState = () => {
    // item with subitems
    if (!!expandable) setOpen(!open);
    // item that is just a link
    else if (isMdDown) setShowMenu(false);
  };

  const handleSubItemClick = () => {
    if (isMdDown) setShowMenu(false);
  };

  useEffect(() => {
    subItem.forEach((item) => {
      if (item.url === firstPageUrlPathname) setOpen(true);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const collapseStyle = isSideMenuCollapsed
    ? { height: 'auto' }
    : !expandable || open
    ? {
        height:
          // contentRef.current?.scrollHeight
          contentRef?.current?.children?.length * 38 + // Height of each sub-item
          // contentRef?.current?.children?.length * 0 + // (pt + pb) of each sub-item
          // (contentRef?.current?.children?.length - 1) * 0 + // 8px here is the vertical gap between sub items.
          // 12 * 2 + // (padding-top: 12px; of wrapper) + (padding-bottom: 12px; of wrapper)
          // 1 + // border-bottom-width of active element
          'px',
      }
    : { height: '0px' };

  const renderNotifBadge = () => {
    // TODO: Below line will be uncommented
    if (!hasNotifBadge) return null;

    const notifElement = <div className={styles.notif_badge}></div>;

    // without tooltip text and just badge
    if (!notifBadgeTooltipText)
      return <div className={styles.notif_badge_wrapper}>{notifElement}</div>;

    return (
      <div className={styles.notif_badge_wrapper}>
        <Tooltip
          placement='bottom-end'
          message={notifBadgeTooltipText}
          additionalStyle={notifBadgeTooltipStyle}
          offsetValue={10}
        >
          {notifElement}
        </Tooltip>
      </div>
    );
  };

  return (
    <div
      // isExternalLink={isExternalLink} link={url}
      data-collapse={isSideMenuCollapsed}
      data-selected={isActive}
      className={styles.menu_item_container}
    >
      <div
        onClick={handleToggleItemState}
        data-selected={isActive}
        data-expandable={expandable}
        data-collapse={isSideMenuCollapsed}
        className={styles.container}
      >
        {renderNotifBadge()}
        {!isSideMenuCollapsed && (
          <div className={styles.icon_and_text_wrapper}>
            {/* <div className={styles.icon_cotainer} data-selected={isActive}>
              <IconUrl />
            </div> */}
            <div className={styles.text_cotainer}>
              <span>{text}</span>
            </div>
          </div>
        )}

        {!isSideMenuCollapsed && !!expandable && (
          <div data-open={open} className={styles.arrow_cotainer}>
            <ChevronDownIcon />
          </div>
        )}
      </div>

      <div
        className={styles.collapse_content}
        ref={contentRef}
        style={collapseStyle}
        data-open={isSideMenuCollapsed ? true : !expandable ? true : open}
        data-collapse={isSideMenuCollapsed}
      >
        {subItem?.map((item) => {
          const SubitemIcon = item.icon;
          return (
            <WithLink
              key={item.text_en}
              isExternalLink={item.isExternalLink}
              link={item.url}
              wrapInsdieDiv
              className={styles.subItem}
              data-selected={item.url === firstPageUrlPathname}
              data-collapse={isSideMenuCollapsed}
              id={!item.url ? item.key : undefined}
              onClick={() => {
                handleSubItemClick();

                if (!!item.closeSecondMenuOnClick) {
                  dispatch(setSecondMenuCollapsed(true));
                }

                if (!!item?.on_click_handler) {
                  item?.on_click_handler({
                    dispatch,
                    k: {
                      text_fa: item.text_fa,
                      text_en: item.text_en,
                      icon: item.icon,
                      key_name: item.key,
                    },
                  });
                }
              }}
            >
              <div
                className={styles.subItem_icon_container}
                data-collapse={isSideMenuCollapsed}
                data-selected={item.url === firstPageUrlPathname}
                title={
                  isSideMenuCollapsed
                    ? getAppLocale() === 'fa'
                      ? item.text_fa
                      : item.text_en
                    : undefined
                }
              >
                <SubitemIcon />
              </div>

              {!isSideMenuCollapsed && (
                <p>{getAppLocale() === 'fa' ? item.text_fa : item.text_en}</p>
              )}

              {!!item?.hasMoreItemsArrow && (
                <ChevronDownIcon
                  className={styles.has_more_items_arrow}
                  data-collapse={isSideMenuCollapsed}
                />
              )}
            </WithLink>
          );
        })}
      </div>
    </div>
  );
}

MenuItem.propTypes = {
  text: PropTypes.string.isRequired,
  iconUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  url: PropTypes.string,
  subItem: PropTypes.array.isRequired,
  setShowMenu: PropTypes.func.isRequired,
};

export default memo(MenuItem);
